import ArrowLeft16 from '@carbon/icons-react/es/arrow--left/16';
import EventSchedule16 from '@carbon/icons-react/es/event--schedule/16';
import Information16 from '@carbon/icons-react/es/information/16';
import PiggyBank24 from '@carbon/icons-react/es/piggy-bank/24';
import { Col, ColProps, Row } from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  BillingCycle,
  LicenseCapLimit,
  RestrictedPlans,
  Subscription,
  SupportedPlans,
  UpgradeTo,
} from 'api/billing/types';
import { Alert } from 'components/atoms/alert';
import { Button } from 'components/atoms/button';
import { PageLoading } from 'components/atoms/loading';
import { Tag } from 'components/atoms/tag';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import { queryClient } from 'queries';
import {
  SUBSCRIPTION_KEY,
  useInvoiceDetailsQuery,
  usePlanName,
  useRemoveScheduledCancellationMutation,
  useRemoveScheduledPlanChangeMutation,
  useScheduleSubscriptionCancellationMutation,
} from 'queries/billing';
import { BillingHelpMessage } from './help-message';
import { InvoiceList } from './invoice-list';
import { BillingEmailsModal, CancelConfirmation } from './modals';
import { PlanSelection } from './plan-selection';
import { BillingSelfServePortal } from './portal';
import { BillingReactivate } from './reactivate';
import { Card, Title } from './styled-components';
import { BillingUpgrade } from './upgrade';
import {
  formatAmount,
  formatInvoiceAmount,
  getCurrentBillingCycle,
  getNextPlan,
  getPlanName,
  getSupportedPlanName,
  getYearlySavings,
} from './utils';

const PlanDetailsRow = styled(Row)`
  padding: 0 0 var(--spacing-06) 0;

  ${Tag} {
    margin: 0 var(--spacing-03);
  }

  .billing-cycle {
    color: var(--text-02);
  }
`;

const FirstRow = styled(Row)`
  height: 136px;
`;

const SecondRow = styled(Row)`
  height: 136px;
  margin-top: var(--spacing-05);
`;

const EmailList = styled.ul`
  list-style: none;
  padding: 0;
`;

const ChangePlanDismiss = styled.div`
  display: flex;
  flex-direction: column;

  Button.close {
    align-self: flex-start;
    margin-left: calc(var(--spacing-05) * -1);
    margin-bottom: var(--layout-03);
  }
`;

const BuyButton = styled(Button)`
  padding: 0;
  height: auto;
`;

const LinkButton = ({
  icon,
  children,
  onClick,
  iframeLoaded,
  loading,
}: {
  icon?: React.ReactElement;
  children: React.ReactElement;
  onClick: () => void;
  iframeLoaded?: boolean;
  loading?: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const _onClick = useCallback(() => {
    setIsLoading(true);
    onClick();
  }, [onClick]);

  useEffect(() => {
    if (iframeLoaded) {
      window.setTimeout(() => setIsLoading(false), 500);
    }
  }, [iframeLoaded]);

  useEffect(() => {
    if (loading === undefined) {
      return;
    }
    setIsLoading(loading);
  }, [loading]);

  return (
    <Button type="link" icon={icon} onClick={_onClick} loading={isLoading}>
      {children}
    </Button>
  );
};

const StyledInvoiceList = styled(InvoiceList)`
  margin-top: '40px';
`;

const CaptionText = styled.div`
  font-size: var(--font-size-sm);
  color: var(--gray-100);
  opacity: 0.7;
`;

const UpgradeCycleButton = styled.div`
  ${Button} {
    color: var(--green-50) !important;
    text-decoration: underline;

    > svg {
      margin-right: var(--spacing-03);
    }
  }
`;

const StyledAlert = styled(Alert)`
  margin-bottom: var(--spacing-05);
`;

export const ActiveSubscriptionSettings = ({
  subscription,
  isExpired,
}: {
  subscription: Subscription;
  isExpired: boolean;
}) => {
  const { data: invoiceDetails, isLoading } = useInvoiceDetailsQuery();
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [showEditEmail, setShowEditEmail] = useState(false);
  const [upgradeTo, setUpgradeTo] = useState<UpgradeTo | null>(null);
  const [loadPortal, setLoadPortal] = useState(false);
  const [reactivateSubscription, setReactivateSubscription] = useState(false);

  const downgradeScheduledCanceledSuccessTitle = useTranslation(
    'billing.downgrade.scheduled.canceled.success.title',
  );
  const downgradeScheduledCanceledSuccessMessage = useTranslation(
    'billing.downgrade.scheduled.canceled.success.message',
  );
  const cancellationScheduledSuccessTitle = useTranslation(
    'billing.cancellation.scheduled.success.title',
  );
  const cancellationScheduledSuccessMessage = useTranslation(
    'billing.cancellation.scheduled.success.message',
  );
  const cancellationScheduledCanceledSuccessTitle = useTranslation(
    'billing.cancellation.scheduled.canceled.success.title',
  );
  const cancellationScheduledCanceledSuccessMessage = useTranslation(
    'billing.cancellation.scheduled.canceled.success.message',
  );

  const {
    mutateAsync: removeScheduledPlanChange,
    isLoading: isRemovingScheduledPlanChange,
  } = useRemoveScheduledPlanChangeMutation();

  const {
    mutateAsync: scheduleCancellation,
    isLoading: isSchedulingCancellation,
  } = useScheduleSubscriptionCancellationMutation();

  const {
    mutateAsync: removeScheduledCancellation,
    isLoading: isRemovingScheduledCancellation,
  } = useRemoveScheduledCancellationMutation();

  const planName = useMemo(
    () => getPlanName(subscription.plan.planName),
    [subscription.plan.planName],
  );

  const billingCycle = useMemo(
    () => getCurrentBillingCycle(subscription.plan.planName),
    [subscription.plan.planName],
  );

  const formatDate = useCallback(
    (date) => moment(date).format('MMM D, YYYY'),
    [],
  );

  const nextPlan = useMemo(
    () => getNextPlan(subscription.plan),
    [subscription.plan],
  );

  const canAddLicenses = useMemo(
    () =>
      planName !== SupportedPlans.Professional ||
      subscription.licenseQuantity < LicenseCapLimit.Professional ||
      (planName as SupportedPlans) !== SupportedPlans.Essentials ||
      subscription.licenseQuantity < LicenseCapLimit.Essentials,
    [planName, subscription.licenseQuantity],
  );

  const dismiss = useCallback(() => setShowChangePlan(false), []);

  const onReactivateDismiss = useCallback(
    () => setReactivateSubscription(false),
    [],
  );

  const onPlanUpgradeClick = useCallback(() => {
    setUpgradeTo({
      plan: nextPlan,
    });
  }, [nextPlan]);

  const onCycleUpgradeClick = useCallback(() => {
    setUpgradeTo({
      billingCycle: BillingCycle.Yearly,
    });
  }, []);

  const onAddLicensesClick = useCallback(() => {
    setUpgradeTo({
      licenseCount: 1,
    });
  }, []);

  const openPortal = useCallback(() => {
    setLoadPortal(true);
  }, []);

  const onCancelScheduledDowngrade = useCallback(async () => {
    try {
      await removeScheduledPlanChange();
      toastService({
        title: downgradeScheduledCanceledSuccessTitle,
        message: downgradeScheduledCanceledSuccessMessage,
      }).success();
      queryClient.invalidateQueries(SUBSCRIPTION_KEY);
    } catch (e) {
      toastService({
        title: '',
        message: '',
      }).error();
    }
  }, [
    downgradeScheduledCanceledSuccessMessage,
    downgradeScheduledCanceledSuccessTitle,
    removeScheduledPlanChange,
  ]);

  const [cancelSubscriptionSelected, setCancelSubscriptionSelected] =
    useState(false);
  const onCancellationClick = useCallback(
    () => setCancelSubscriptionSelected(true),
    [],
  );

  const onScheduleCancellation = useCallback(
    async (cancellationFormValues) => {
      try {
        await scheduleCancellation(cancellationFormValues);
        toastService({
          title: cancellationScheduledSuccessTitle,
          message: cancellationScheduledSuccessMessage,
        }).success();
        queryClient.invalidateQueries(SUBSCRIPTION_KEY);
      } catch (e) {
        toastService({
          title: '',
          message: '',
        }).error();
      } finally {
        setCancelSubscriptionSelected(false);
      }
    },
    [
      cancellationScheduledSuccessMessage,
      cancellationScheduledSuccessTitle,
      scheduleCancellation,
    ],
  );

  const onRemoveScheduledCancellation = useCallback(async () => {
    try {
      await removeScheduledCancellation();
      toastService({
        title: cancellationScheduledCanceledSuccessTitle,
        message: cancellationScheduledCanceledSuccessMessage,
      }).success();
      queryClient.invalidateQueries(SUBSCRIPTION_KEY);
    } catch (e) {
      toastService({
        title: '',
        message: '',
      }).error();
    }
  }, [
    cancellationScheduledCanceledSuccessMessage,
    cancellationScheduledCanceledSuccessTitle,
    removeScheduledCancellation,
  ]);

  const onEditEmail = useCallback(() => {
    setShowEditEmail(true);
  }, []);

  if (isLoading) {
    return <PageLoading css />;
  }

  const CurrentSubscriptionCard = () => {
    const nextPlanName = usePlanName(nextPlan);

    return (
      <Card span={8}>
        <div>
          <Title>
            <FormattedMessage
              id="billing.current.subscription.title"
              values={{
                billingCycle: billingCycle.replace('-', '_'),
              }}
            />
          </Title>
          <b>
            {formatDate(subscription.startDate)} -{' '}
            {formatDate(subscription.endDate)}
          </b>
          {invoiceDetails && (
            <CaptionText>
              {formatInvoiceAmount(invoiceDetails)} &#183;{' '}
              <FormattedMessage
                id="billing.subscription.license.details"
                values={{
                  count: subscription?.licenseQuantity,
                }}
              />
            </CaptionText>
          )}
        </div>
        <div>
          {nextPlan ? (
            <LinkButton onClick={onPlanUpgradeClick} iframeLoaded>
              <FormattedMessage
                id="billing.upgrade.to.plan"
                values={{
                  planName: nextPlanName,
                }}
              />
            </LinkButton>
          ) : null}
        </div>
      </Card>
    );
  };

  const TotalLicenseCard = () => {
    return (
      <Card span={8}>
        <div>
          <Title>
            <FormattedMessage id="billing.subscription.total.licenses" />
          </Title>
          <b>{subscription.licenseQuantity}</b>
        </div>
        {canAddLicenses && (
          <div>
            <Button type="link" onClick={onAddLicensesClick}>
              <FormattedMessage id="billing.add.licenses.link" />
            </Button>
          </div>
        )}
      </Card>
    );
  };

  const MoreOptionsCard = (props: ColProps) => {
    const isCancellationScheduled =
      subscription?.subscriptionMeta?.planCancellationScheduled;

    const isDowngradeScheduled =
      subscription?.subscriptionMeta?.planChangesScheduled;

    return (
      <Card span={8} className="more-options-card" {...props}>
        <div>
          <Title>
            <FormattedMessage id="billing.subscription.more.options" />
          </Title>
          <LinkButton onClick={openPortal} iframeLoaded>
            <FormattedMessage id="billing.change.payment.method" />
          </LinkButton>
          {!(isDowngradeScheduled || isCancellationScheduled) && (
            <LinkButton onClick={() => setShowChangePlan(true)} iframeLoaded>
              <FormattedMessage id="billing.change.plan" />
            </LinkButton>
          )}
          {!isExpired && !isCancellationScheduled && (
            <LinkButton onClick={onCancellationClick}>
              <FormattedMessage id="billing.cancel.subscription" />
            </LinkButton>
          )}
        </div>
      </Card>
    );
  };

  const NextScheduleCard = () => {
    const { subscriptionMeta } = subscription;
    const nextPlanName = usePlanName(
      subscriptionMeta?.nextSubscription != null
        ? getPlanName(subscriptionMeta?.nextSubscription?.plan.planName)
        : null,
    );

    if (!subscriptionMeta) {
      return <></>;
    }
    const { nextSubscription, planChangesScheduled } = subscriptionMeta;

    if (!nextSubscription) {
      return <></>;
    }

    return (
      <Card span={8}>
        <div>
          <Title>
            <FormattedMessage id="billing.subscription.next.renewal.on" />
          </Title>
          <b>{formatDate(moment(nextSubscription.nextBillingAt))}</b>
          <CaptionText>
            <FormattedMessage
              id="billing.renewal.details"
              values={{
                planName: nextPlanName,
                billingCycle: getCurrentBillingCycle(
                  nextSubscription.plan.planName,
                ).replace('-', '_'),
                licenseCount: nextSubscription.licenseQuantity,
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          </CaptionText>
        </div>
        {planChangesScheduled && (
          <div>
            <Button
              type="link"
              onClick={onCancelScheduledDowngrade}
              loading={isRemovingScheduledPlanChange}
              icon={<EventSchedule16 />}
            >
              <FormattedMessage id="billing.cancel.scheduled.downgrade" />
            </Button>
          </div>
        )}
      </Card>
    );
  };

  const CancellationScheduledCard = () => {
    return (
      <Card span={8}>
        <div>
          <Title>
            <FormattedMessage id="billing.subscription.cancellation.scheduled.on" />
          </Title>
          <b>{formatDate(moment(subscription.endDate).add(1, 'day'))}</b>
        </div>
        <div>
          <Button
            type="link"
            onClick={onRemoveScheduledCancellation}
            loading={isRemovingScheduledCancellation}
            icon={<EventSchedule16 />}
          >
            <FormattedMessage id="billing.cancel.subscription.cancellation" />
          </Button>
        </div>
      </Card>
    );
  };

  const NextInvoiceTotalCard = () => {
    const { subscriptionMeta } = subscription;

    if (!subscriptionMeta) {
      return <></>;
    }
    const { nextInvoiceTotal } = subscriptionMeta;

    if (!nextInvoiceTotal) {
      return <></>;
    }

    return (
      <Card span={8}>
        <div>
          <Title>
            <FormattedMessage id="billing.next.invoice.total" />
          </Title>
          <b>
            {formatAmount(nextInvoiceTotal)} {subscription.currency}
          </b>
        </div>
        <UpgradeCycleButton>
          {billingCycle !== BillingCycle.Yearly &&
          ![
            SupportedPlans.Enterprise,
            SupportedPlans['Enterprise-V2'],
            RestrictedPlans['Enterprise-Tiered'],
            RestrictedPlans['Enterprise-Tiered_V2'],
          ].includes(planName) ? (
            <>
              <LinkButton
                icon={<PiggyBank24 />}
                onClick={onCycleUpgradeClick}
                iframeLoaded
              >
                <FormattedMessage
                  id="billing.upgrade.cycle"
                  values={{
                    savings: getYearlySavings(
                      getSupportedPlanName(subscription.plan.planName),
                      subscription.licenseQuantity,
                    ),
                  }}
                />
              </LinkButton>
            </>
          ) : null}
        </UpgradeCycleButton>
      </Card>
    );
  };

  const EmailCard = () => {
    return (
      <Card span={8}>
        <div>
          <Title>
            <FormattedMessage id="billing.invoices.sent.to" />
          </Title>
          <EmailList>
            <li>
              <b>{invoiceDetails?.primaryEmail}</b>
            </li>
            {invoiceDetails?.secondaryEmails &&
              invoiceDetails?.secondaryEmails?.length > 0 && (
                <li>
                  {invoiceDetails?.secondaryEmails[0]}{' '}
                  {invoiceDetails?.secondaryEmails?.length > 1 && (
                    <FormattedMessage
                      id="editor.plus_n_more"
                      values={{
                        count: invoiceDetails?.secondaryEmails?.length - 1,
                      }}
                    />
                  )}
                </li>
              )}
          </EmailList>
        </div>
        <div>
          <Button type="link" onClick={onEditEmail}>
            <FormattedMessage id="billing.edit.emails" />
          </Button>
        </div>
      </Card>
    );
  };

  const ExpiryBanner = () => {
    const _planName = usePlanName(planName);

    return (
      <>
        <StyledAlert
          type="error"
          showIcon
          icon={<Information16 />}
          message={
            <FormattedMessage
              id="billing.subscription.canceled.banner"
              values={{
                b: (chunks) => <b>{chunks}</b>,
                planName: _planName,
                subscriptionEndDate: moment(
                  subscription?.subscriptionMeta?.subscriptionUpdatedAt,
                ).format('DD MMM YYYY'),
                link: (chunks) => (
                  <u>
                    <BuyButton
                      type="text"
                      onClick={() => setReactivateSubscription(true)}
                    >
                      {chunks}
                    </BuyButton>
                  </u>
                ),
              }}
            />
          }
        />
      </>
    );
  };

  const CardRows = () => {
    return (
      <>
        {isExpired ? (
          <FirstRow>
            <EmailCard />
            <MoreOptionsCard span={16} />
          </FirstRow>
        ) : (
          <>
            <FirstRow>
              <CurrentSubscriptionCard />
              <TotalLicenseCard />
              <MoreOptionsCard />
            </FirstRow>

            <SecondRow>
              {subscription?.subscriptionMeta?.planCancellationScheduled ? (
                <CancellationScheduledCard />
              ) : (
                <NextScheduleCard />
              )}
              {!subscription?.subscriptionMeta?.planCancellationScheduled && (
                <NextInvoiceTotalCard />
              )}
              <EmailCard />
            </SecondRow>
          </>
        )}
      </>
    );
  };

  return (
    <>
      {showChangePlan && (
        <ChangePlanDismiss>
          <Button
            type="link"
            icon={<ArrowLeft16 />}
            onClick={dismiss}
            className="close"
          >
            <FormattedMessage id="go.back" />
          </Button>
          <PlanSelection subscription={subscription} close={dismiss} />
        </ChangePlanDismiss>
      )}
      {!showChangePlan && (
        <>
          {isExpired && <ExpiryBanner />}
          <PlanDetailsRow>
            <Col flex="auto">
              <FormattedMessage id="billing.current.plan" />:
              <Tag color="green">
                <FormattedMessage
                  id={`billing.plan.${planName}`.toLowerCase()}
                />
              </Tag>
              <span className="billing-cycle">
                <FormattedMessage
                  id="billing.cycle.text"
                  values={{
                    billingCycle: billingCycle.replace('-', '_'),
                  }}
                />
              </span>
            </Col>

            <Col flex="none">
              <BillingHelpMessage />
            </Col>
          </PlanDetailsRow>
          <CardRows />
          <StyledInvoiceList
            subscriptionId={subscription.subscriptionId}
            currencyCode={subscription.currency}
          />
        </>
      )}
      {upgradeTo !== null && (
        <BillingUpgrade
          upgradeTo={upgradeTo}
          subscription={subscription}
          onDismiss={() => setUpgradeTo(null)}
        />
      )}
      {reactivateSubscription && (
        <BillingReactivate
          subscription={subscription}
          onDismiss={onReactivateDismiss}
        />
      )}
      {loadPortal && (
        <BillingSelfServePortal onDismiss={() => setLoadPortal(false)} />
      )}
      {showEditEmail && invoiceDetails && (
        <BillingEmailsModal
          invoiceDetails={invoiceDetails}
          close={() => setShowEditEmail(false)}
        />
      )}
      {cancelSubscriptionSelected && (
        <CancelConfirmation
          currentPlan={planName}
          subscriptionEndDate={subscription.endDate}
          isScheduling={isSchedulingCancellation}
          onSubmit={onScheduleCancellation}
          onCancel={() => setCancelSubscriptionSelected(false)}
        />
      )}
    </>
  );
};
